import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Sparkles, Loader2, CheckCircle2 } from "lucide-react";

const RECOMMENDATIONS = {HIRE:"Hire",MANAGED_HUMAN:"Managed Human",AI:"AI Employee",HYBRID:"Hybrid",TRAIN:"Train Existing",AUTOMATE:"Automate",OUTSOURCE:"Outsource",MANAGED_TEAM:"Managed Team"};

export default function WorkforceAssessment() {
  const nav = useNavigate();
  const [f, setF] = useState({what_work:"", business_problem:"", ideal_outcome:"", weekly_hours:40, customer_facing:false, repetitive_predictable:false, creative_judgment:false, industry_specific:false, budget_monthly:null, timeline:"", us_hours_required:false, remote_ok:true});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const upd = (k,v)=>setF(prev=>({...prev,[k]:v}));

  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post("/employer/workforce-assessments", f); setResult(data); toast.success(`Recommendation: ${RECOMMENDATIONS[data.primary_recommendation]}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const act = async (action) => {
    try { await api.post(`/employer/workforce-assessments/${result.id}/action`, {action}); toast.success(action.replace(/_/g," ")); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-slate-100 px-6 py-3 flex items-center gap-3">
        <button onClick={()=>nav("/employer/dashboard")} className="text-xs inline-flex items-center gap-1"><ArrowLeft size={12}/> Back</button>
        <div className="font-display font-bold text-sm">Workforce Needs Assessment</div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-8">
        {!result ? (
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
            <h1 className="font-display text-2xl font-bold text-slate-900">Help me build my workforce</h1>
            <p className="text-sm text-slate-500">Answer a few questions and ConnectMyHub will recommend the right mix — hire, managed team, AI, hybrid, train, automate, or outsource.</p>
            <Fld k="what_work" label="Describe the work that needs to be done" textarea f={f} setF={upd} required/>
            <Fld k="business_problem" label="What business problem does this solve?" textarea f={f} setF={upd}/>
            <Fld k="ideal_outcome" label="What does success look like in 90 days?" textarea f={f} setF={upd}/>
            <div className="grid md:grid-cols-2 gap-3">
              <Fld k="weekly_hours" label="Weekly hours needed" type="number" f={f} setF={upd}/>
              <Fld k="budget_monthly" label="Monthly budget (USD)" type="number" f={f} setF={upd}/>
              <Fld k="timeline" label="Timeline" f={f} setF={upd}/>
            </div>
            <div className="space-y-1.5">
              <Chk k="customer_facing" label="Customer-facing work" f={f} setF={upd}/>
              <Chk k="repetitive_predictable" label="Highly repetitive / predictable work" f={f} setF={upd}/>
              <Chk k="creative_judgment" label="Requires human judgment or creativity" f={f} setF={upd}/>
              <Chk k="industry_specific" label="Industry-specific knowledge required" f={f} setF={upd}/>
              <Chk k="us_hours_required" label="Must work US business hours" f={f} setF={upd}/>
              <Chk k="remote_ok" label="Remote is OK" f={f} setF={upd}/>
            </div>
            <button data-testid="wa-submit" onClick={submit} disabled={busy || !f.what_work} className="h-11 px-6 rounded-md bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">{busy?<Loader2 size={14} className="animate-spin"/>:<Sparkles size={14}/>} Get recommendation</button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">ConnectMyHub Recommendation</div>
              <div className="font-display text-4xl font-bold text-slate-900 mb-3" data-testid="wa-primary">{RECOMMENDATIONS[result.primary_recommendation]}</div>
              <p className="text-slate-700 mb-3">{result.reasoning}</p>
              {result.ai_note && <div className="p-3 rounded bg-sky-50 border border-sky-200 text-sm text-sky-900 italic">{result.ai_note}</div>}
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Full ranking</div>
                <div className="space-y-1.5">{result.ranked.map(r => (
                  <div key={r.kind} className="flex items-center gap-3">
                    <div className="w-32 text-sm text-slate-700">{RECOMMENDATIONS[r.kind]}</div>
                    <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-sky-500" style={{width:`${Math.max(0,Math.min(100,r.score))}%`}}></div></div>
                    <div className="text-xs font-mono w-10 text-right">{r.score}</div>
                  </div>
                ))}</div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button data-testid="wa-accept" onClick={()=>act("ACCEPT")} className="h-10 px-4 rounded bg-emerald-600 text-white text-sm font-semibold inline-flex items-center gap-1.5"><CheckCircle2 size={13}/> Accept plan</button>
              <button data-testid="wa-changes" onClick={()=>act("REQUEST_CHANGES")} className="h-10 px-4 rounded border border-amber-300 bg-amber-50 text-amber-800 text-sm font-semibold">Request changes</button>
              <button data-testid="wa-talk" onClick={()=>act("TALK_TO_STAFFHUB")} className="h-10 px-4 rounded border border-slate-300 text-sm font-semibold">Talk to ConnectMyHub</button>
            </div>
          </div>
        )}
      </main>
      <Toaster position="top-right" richColors/>
    </div>
  );
}
const Fld = ({k, label, f, setF, textarea, type="text", required}) => (
  <div><label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">{label}{required?"*":""}</label>
    {textarea ? <textarea rows={3} data-testid={`wa-${k}`} value={f[k]||""} onChange={e=>setF(k,e.target.value)} className="w-full px-3 py-2 rounded border border-slate-300 text-sm resize-none"/> :
      <input type={type} data-testid={`wa-${k}`} value={f[k]||""} onChange={e=>setF(k, type==="number"?(e.target.value?parseFloat(e.target.value):null):e.target.value)} className="w-full h-10 px-3 rounded border border-slate-300 text-sm"/>}
  </div>
);
const Chk = ({k, label, f, setF}) => (
  <label className="inline-flex items-center gap-2 text-sm text-slate-700 mr-4"><input type="checkbox" data-testid={`wa-${k}`} checked={!!f[k]} onChange={e=>setF(k,e.target.checked)}/> {label}</label>
);
